import type { ConversationItemLabelProps } from './index.types';
import { MobXProviderContext, observer } from 'mobx-react';
import * as React from 'react';
import { Loader, Message } from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import { isNullOrUndefined } from 'Utils/typeGuards';
import { ConversationItemName } from './ConversationItemName';

// Modified copy of src/app/components/ChatList/ChatListItemText/ChatListItemTextRenderer.tsx
const ConversationItemLabel: React.FC<ConversationItemLabelProps> = ({
  conversation,
  isConversationSelect,
}) => {
  const {
    ui: uiStore,
    conversation: conversationStore,
    person: personStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const isUnread =
    uiStore.selectConversationUnreadCounts(conversation.id).unreadMessages > 0;

  const renderPersonName = (personId: number, isOwnChat = false) => {
    // Trigger loading the person if not already loaded
    void personStore.loadPersonByIdGetIfMissingGet(personId.toString());

    // Get the person directly from the MobX store - this will reactively update
    const response = personStore.selectPersonValueById(personId);

    if (!response) {
      return <Loader indeterminate active size="tiny" />;
    }

    const displayName =
      response?.name ||
      (response?.firstName && response?.lastName
        ? `${response.firstName} ${response.lastName}`
        : conversation.topic);

    const emoji = uiStore.selectPersonMessageStatus(response.sourceId);

    return (
      <ConversationItemName
        key={`chat-list-item-${conversation.id}-${emoji?.title}`}
        bold={isUnread || isConversationSelect}
        title={displayName}
        isInactive={response?.deleted}
        emoji={response?.deleted ? undefined : emoji}
        isOwnChat={isOwnChat}
      />
    );
  };

  if (
    conversation?.grouping === 'Channel' ||
    conversation?.grouping === 'Group'
  ) {
    if (
      (conversation.topic?.includes('Group') || !conversation.topic) &&
      conversation.grouping === 'Group'
    ) {
      const conversationName = conversationStore.createProperName(
        conversation.participants,
        undefined,
        false
      );

      return (
        <ConversationItemName
          key={`chat-list-item-${conversation.id}`}
          bold={isUnread || isConversationSelect}
          title={conversationName}
        />
      );
    }

    return (
      <ConversationItemName
        key={`chat-list-item-${conversation.id}`}
        bold={isUnread || isConversationSelect}
        title={conversation.topic}
      />
    );
  }

  if (conversation?.grouping === 'OneOnOne') {
    const otherPtc = conversation?.participants.find(
      ({ personId }) => personId !== personStore.loggedInPersonId
    );
    const mySelfPtc = conversation?.participants.find(
      ({ personId }) => personId === personStore.loggedInPersonId
    );

    if (otherPtc) {
      if (otherPtc.phone) {
        const phoneNumWithNationalCode = formatNumberWithNationalCode(
          otherPtc.phone
        );

        const otherParticipantContacts = personStore.getAllContactsWithSameNum(
          otherPtc.phone
        );

        if (isNullOrUndefined(otherParticipantContacts)) {
          return (
            <ConversationItemName
              key={`chat-list-item-${conversation.id}`}
              bold={isUnread || isConversationSelect}
              title={phoneNumWithNationalCode}
              subtitle="[SMS]"
            />
          );
        }

        return otherParticipantContacts.case({
          pending: () => null,
          fulfilled: (contacts) => {
            return (
              <ConversationItemName
                key={`chat-list-item-${conversation.id}`}
                bold={isUnread || isConversationSelect}
                title={contacts[0]?.name || phoneNumWithNationalCode}
                subtitle="[SMS]"
              />
            );
          },
        });
      }

      if (isFinite(otherPtc.personId)) {
        return renderPersonName(otherPtc.personId, false);
      }

      return (
        <Message
          visible
          error
          content={
            'Error: Empty personId and phone for Participant ' + otherPtc.id
          }
        />
      );
    }

    if (mySelfPtc && isFinite(mySelfPtc.personId)) {
      return renderPersonName(mySelfPtc.personId, true);
    }

    return <Message visible error content="Other Participant not found!" />;
  }

  return null;
};

export default observer(ConversationItemLabel);
